import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, AlertCircle, RefreshCw, X, Filter, Box, ArrowUpDown } from 'lucide-react';
import api from './axiosConfig';
import toast from 'react-hot-toast';
import { usePermissions } from './usePermissions';
import { useTranslation } from 'react-i18next'; // 🎯 الترجمة
import { exportToExcel } from './exportUtils'; 
import { useFontSize } from './FontSizeContext';

const IncidentList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canCreate } = usePermissions('incidents'); // 🛡️ الصلاحيات
  
  // --- Session Data ---
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';

  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  // --- State ---
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showAllSites, setShowAllSites] = useState(searchParams.get('all_sites') === 'true');

  // --- URL Params (Persistence) ---
  const page = parseInt(searchParams.get('page')) || 1;
  const limit = 50;
  const q = searchParams.get('q') || "";
  const status = searchParams.get('status') || "";
  const reported_by = searchParams.get('reported_by') || "";
  const sortBy = searchParams.get('sortBy') || "report_date";
  const sortDir = searchParams.get('sortDir') || "desc";

  const fetchIncidents = useCallback(async () => {
    setLoading(true);
    try {
      const skip = (page - 1) * limit;
      const effectiveSite = !showAllSites && !searchParams.get('site_id') ? sessionSite : searchParams.get('site_id');

      const res = await api.get('/incidents', {
        params: {
          org_id: sessionOrg,
          site_id: effectiveSite,
          all_sites: showAllSites,
          search: q,
          status: status,
          reported_by: reported_by,
          skip: skip,
          limit: limit,
          sortBy: sortBy,
          sortDir: sortDir
        }
      });
      
      setIncidents(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(t('common.load_error', "Failed to load incidents"));
    } finally {
      setLoading(false);
    }
  }, [page, limit, q, status, reported_by, sortBy, sortDir, showAllSites, searchParams, sessionSite, sessionOrg, t]);

  useEffect(() => {
    const timer = setTimeout(() => fetchIncidents(), 100);
    return () => clearTimeout(timer);
  }, [fetchIncidents]);

  // --- Filter Handlers ---
  const updateFilters = (newFilters) => {
    const params = Object.fromEntries(searchParams.entries());
    const updated = { ...params, ...newFilters };
    Object.keys(updated).forEach(key => !updated[key] && delete updated[key]);
    setSearchParams(updated);
  };

  const handleSort = (column) => {
    const direction = sortBy === column && sortDir === 'asc' ? 'desc' : 'asc';
    updateFilters({ sortBy: column, sortDir: direction, page: 1 });
  };

  const handleClearFilters = () => {
    setSearchParams({});
    setShowAllSites(false);
  };

  const handleRowClick = (id) => {
    const activeFilters = Object.fromEntries(searchParams.entries());
    const effectiveSite = !showAllSites && !activeFilters.site_id ? sessionSite : activeFilters.site_id;
    let navParams = { ...activeFilters, org_id: sessionOrg };
    if (effectiveSite) navParams.site_id = effectiveSite; else delete navParams.site_id;
    if (showAllSites) navParams.all_sites = true;
    
    const queryParams = new URLSearchParams(navParams).toString();
    navigate(`/incidents/${id}${queryParams ? '?' + queryParams : ''}`);
  };

  // --- Sidebar Events ---
  useEffect(() => {
    const openAllSites = () => { setShowAllSites(true); updateFilters({ page: 1, all_sites: true }); };
    const viewMySite = () => { setShowAllSites(false); updateFilters({ page: 1, all_sites: false }); };
    
    const exportData = () => {
        const activeFilters = Object.fromEntries(searchParams.entries());
        const effectiveSite = !showAllSites && !activeFilters.site_id ? sessionSite : activeFilters.site_id;
        let exportParams = { ...activeFilters, org_id: sessionOrg, search: q };
        if (effectiveSite) exportParams.site_id = effectiveSite; else delete exportParams.site_id;

        const columnsMap = {
            "ticket_id": t('incidents.fields.ticket_id', 'Ticket ID'),
            "description": t('common.description', 'Summary'),
            "status": t('common.status', 'Status'),
            "priority": t('incidents.fields.priority', 'Priority'),
            "reported_by": t('incidents.fields.reported_by', 'Reported By'),
            "report_date": t('common.date', 'Date')
        };
        exportToExcel('incidents', exportParams, columnsMap, t);
    };

    window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
    window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
    window.addEventListener('GLOBAL_EXPORT', exportData);
    
    return () => {
      window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
      window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
      window.removeEventListener('GLOBAL_EXPORT', exportData);
    };
  }, [searchParams, showAllSites, sessionSite, sessionOrg, q, t]);

  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <div className={`flex flex-col h-full bg-[#f3f4f6]  overflow-hidden uppercase tracking-tighter ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="bg-white p-3 border-b flex justify-between items-center shadow-sm z-20">
        <div className={`flex items-center gap-4 ${isRTL ? 'mr-4' : 'ml-4'}`}>
          <h1 className="text-[#005495] font-black italic text-lg flex items-center gap-2">
            <AlertCircle size={24} className="text-orange-500" /> {t('incidents.list.title', 'Incidents Registry')}
            <span className={`text-[0.9em] px-2 py-0.5 rounded-full border not-italic font-bold ml-2 ${showAllSites ? 'bg-red-50 text-red-600 border-red-200' : 'bg-blue-50 text-blue-600 border-blue-200'}`}>
                {showAllSites ? t('common.global_view', 'GLOBAL VIEW') : `${t('common.site', 'SITE')}: ${sessionSite}`}
            </span>
          </h1>
        </div>
        
        <div className={`flex gap-3 items-center ${isRTL ? 'ml-4' : 'mr-4'}`}>
          <button onClick={handleClearFilters} className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-red-500 font-bold transition-colors">
            <X size={16}/> {t('common.clear_filters', 'CLEAR FILTERS')}
          </button>
          <button onClick={() => fetchIncidents()} className="p-2 text-gray-400 hover:text-blue-600 transition-colors" title="Refresh">
            <RefreshCw size={16} className={loading ? "animate-spin text-orange-500" : ""} />
          </button>
          {canCreate && (
            <button onClick={() => navigate('/incidents/new')} className="bg-[#005495] text-white px-6 py-2 rounded font-black hover:bg-[#1a3353] shadow-md transition-all active:scale-95 flex items-center gap-2">
              <Plus size={14}/> {t('incidents.list.new', 'NEW INCIDENT')}
            </button>
          )}
        </div>
      </div>

      {/* Table Area */}
      <div className="flex-1 overflow-auto p-4 custom-scrollbar">
        <div className="bg-white border shadow-sm rounded-sm overflow-hidden min-h-[450px]">
          <table className="w-full text-left border-collapse">
            <thead className="bg-[#f8f9fa] border-b text-gray-700 font-black uppercase text-[0.9em] sticky top-0 z-10 tracking-widest">
              <tr>
                <SortableHeader label={t('incidents.fields.ticket_id', 'Ticket ID')} sortKey="ticket_id" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                <SortableHeader label={t('assets.headers.desc', 'Summary')} sortKey="description" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                <SortableHeader label={t('assets.headers.status', 'Status')} sortKey="status" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
                <SortableHeader label={t('incidents.fields.priority', 'Priority')} sortKey="priority" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
                <SortableHeader label={t('incidents.fields.reported_by', 'Reported By')} sortKey="reported_by" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                <SortableHeader label={t('sr.fields.reportdate', 'Date')} sortKey="report_date" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
                <th className="p-3 border-x border-gray-200 text-center w-24">{t('common.site', 'Site')}</th>
              </tr>
              {/* Filter Row */}
              <tr className="bg-white border-b border-gray-200">
                <td className="p-1 border-x border-gray-100">
                  <div className={`relative flex items-center `}>
                    <Search className={`absolute text-gray-400 ${isRTL ? 'right-2' : 'left-2'}`} size={12} />
                    <FilterInput value={q} onChange={v => updateFilters({ q: v, page: 1 })} placeholder={t('common.search')} className={isRTL ? 'pr-6' : 'pl-6'}/>
                  </div>
                </td>
                <td className="p-1 border-x border-gray-100 bg-gray-50/30"></td>
                <td className="p-1 border-x border-gray-100">
                  <select className={`w-full p-1.5 text-blue-900 font-bold outline-none bg-transparent cursor-pointer ${isRTL ? 'text-right' : 'text-center'}`} value={status} onChange={e => updateFilters({ status: e.target.value, page: 1 })}>
                    <option value="">{t('common.all', 'ALL')}</option>
                    <option value="NEW">NEW</option>
                    <option value="INPRG">INPRG</option>
                    <option value="RESOLVED">RESOLVED</option>
                    <option value="CLOSED">CLOSED</option>
                  </select>
                </td>
                <td className="p-1 border-x border-gray-100 bg-gray-50/30"></td>
                <td className="p-1 border-x border-gray-100"><FilterInput value={reported_by} onChange={v => updateFilters({ reported_by: v, page: 1 })} /></td>
                <td colSpan="2" className="p-1 border-x border-gray-100 bg-gray-50/30"></td>
              </tr>
            </thead>
            
            <tbody className="divide-y divide-gray-100 font-bold">
              {loading ? (
                <tr><td colSpan="7" className="p-20 text-center animate-pulse font-black text-gray-300 uppercase tracking-[0.5em]">{t('common.syncing', 'Syncing Data...')}</td></tr>
              ) : incidents.length === 0 ? (
                <tr><td colSpan="7" className="p-20 text-center text-gray-400 italic font-black uppercase tracking-widest">{t('common.no_records', 'No records found')}</td></tr>
              ) : incidents.map((inc) => (
                <tr 
                  key={inc.id} 
                  onClick={() => handleRowClick(inc.id)}
                  className={`hover:bg-blue-50/50 cursor-pointer transition-all group ${isRTL ? 'text-right' : 'text-left'}`}
                >
                  <td className="p-3 border-x border-gray-50 font-black text-[#005495] group-hover:underline">{inc.ticket_id}</td>
                  <td className="p-3 border-x border-gray-50 text-gray-600 truncate max-w-xs">{inc.description}</td>
                  <td className="p-3 border-x border-gray-50 text-center">
                    <span className={`px-3 py-1 rounded text-[9px] font-black text-white shadow-sm ${
                      inc.status === 'NEW' ? 'bg-orange-500' : 
                      inc.status === 'CLOSED' || inc.status === 'RESOLVED' ? 'bg-green-600' : 'bg-blue-600'
                    }`}>
                      {inc.status}
                    </span>
                  </td>
                  <td className="p-3 border-x border-gray-50 text-center">
                      <span className={`font-black ${inc.priority === 1 ? 'text-red-600' : 'text-gray-500'}`}>{inc.priority}</span>
                  </td>
                  <td className="p-3 border-x border-gray-50 italic text-gray-500">{inc.reported_by || '---'}</td>
                  <td className="p-3 border-x border-gray-50 text-center text-gray-400 font-mono">{new Date(inc.report_date).toLocaleDateString()}</td>
                  <td className="p-3 border-x border-gray-50 text-center text-gray-600">{inc.site_id || '---'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Pagination Footer */}
      <div className={`bg-white border-t border-gray-200 p-2 px-8 flex justify-between items-center shadow-sm z-30 font-black text-[10px] text-gray-500 `}>
        <div className={`flex items-center gap-6 `}>
            <div className={`flex items-center gap-2 `}>
                <Box size={14} className="text-blue-500"/>
                <span>{t('common.showing', 'Showing')}: <span className="text-blue-700">{(page-1)*limit+1}-{Math.min(page*limit, total)}</span></span>
            </div>
            <div className={`flex items-center gap-2 border-gray-200 ${isRTL ? 'border-r pr-6 flex-row-reverse' : 'border-l pl-6'}`}>
                <Filter size={14} className="text-orange-500"/>
                <span>{t('common.total', 'Total')}: <span className="text-orange-600">{total}</span></span>
            </div>
        </div>

        <div className={`flex items-center gap-4 `}>
            <div className={`flex items-center gap-1 bg-gray-50 rounded px-2 py-1 border border-gray-200 `}>
                <button disabled={page === 1} onClick={() => updateFilters({page: 1})} className="p-1.5 hover:text-blue-600 disabled:opacity-30 transition-all"><ChevronsLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <button disabled={page === 1} onClick={() => updateFilters({page: page - 1})} className="p-1.5 hover:text-blue-600 disabled:opacity-30 transition-all"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <div className="px-4 text-[11px] font-mono border-x border-gray-200 mx-1">
                    <span className="text-blue-600 text-sm">{page}</span> / {totalPages}
                </div>
                <button disabled={page >= totalPages} onClick={() => updateFilters({page: page + 1})} className="p-1.5 hover:text-blue-600 disabled:opacity-30 transition-all"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <button disabled={page >= totalPages} onClick={() => updateFilters({page: totalPages})} className="p-1.5 hover:text-blue-600 disabled:opacity-30 transition-all"><ChevronsRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
        </div>
      </div>
    </div>
  );
};

// --- Helper Components ---
const SortableHeader = ({ label, sortKey, sortBy, sortDir, onSort, align = "left", isRTL }) => (
  <th className={`p-3 border-x border-gray-200 cursor-pointer hover:bg-gray-200 transition-colors text-${align === 'center' ? 'center' : (isRTL ? 'right' : 'left')}`} onClick={() => onSort(sortKey)}>
      <div className={`flex items-center gap-2 ${align === 'center' ? 'justify-center' : ''} `}>
          {label}
          <ArrowUpDown size={12} className={sortBy === sortKey ? 'text-blue-500' : 'text-gray-300'} />
      </div>
  </th>
);

const FilterInput = ({ value, onChange, placeholder, center = false, className = "" }) => (
  <input 
    className={`w-full p-1.5 text-blue-900 font-bold outline-none bg-transparent placeholder-gray-300 ${center ? 'text-center' : ''} ${className}`}
    placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} 
  />
);

export default IncidentList;